import * as React from 'react';
import { useContext } from 'react';
import { useTranslation } from 'next-i18next';
import Block from 'ap-admin-components/lib/Components/Block';
import Buttons from 'ap-admin-components/lib/Components/Buttons';
import LayoutOverlay from 'ap-admin-components/lib/Components/LayoutOverlay';
import Overlay from 'ap-admin-components/lib/Components/Overlay';
import Button from 'ap-admin-components/lib/Components/Button';
import Form from 'ap-admin-components/lib/Components/Form';
import ControlInput from 'ap-admin-components/lib/Components/ControlInput';
import { useRouter } from 'next/router';
import ControlSelect from 'ap-admin-components/lib/Components/ControlSelect';
import { Field, Formik } from 'formik';
import { ChannelContext } from '@/components/channel/Channel';
import { OverlayType } from '@/lib/enums/OverlayType.enum';
import { CreateChannelForm } from '@/lib/types';
import { fetchData } from '@/lib/api/fetch';
import { AuthContext } from '@/auth/Authentication';
import { AppToaster } from '@/src/pages/_app';
import { translationNamespace } from '@/lib/config';
import useOverlayActions from '@/lib/hooks/useOverlayActions';
import FileUploadField from '@/components/form/common/FileUploadField';

const createChannel = async (jwt: string, channel: CreateChannelForm) =>
  fetchData<CreateChannelForm>('/channel', {
    method: 'POST',
    jwt,
    body: channel,
  });

const name = OverlayType.channel_create;

const OverlayChannelCreate = () => {
  const { t } = useTranslation(translationNamespace);
  const router = useRouter();

  const { jwt } = useContext(AuthContext);
  const { creatableIds } = useContext(ChannelContext);

  const { isOpen, closeAll } = useOverlayActions();

  const submit = async (values: CreateChannelForm) => {
    const result = await createChannel(jwt!, values);
    if (result.error) {
      AppToaster.addToast({
        intent: 'danger',
        text: t('channel.error'),
      });
    } else {
      AppToaster.addToast({
        intent: 'success',
        text: t('channel.success'),
      });
      void router.push(`/${values.id}`);
    }
  };

  return (
    <Overlay
      layout="dialog"
      isOpen={isOpen(name)}
      zIndex={200}
      doClose={closeAll}
    >
      <LayoutOverlay
        title={t('channel.addNew')}
        scrollableContent={true}
      >
        <Block>
          <Formik
            initialValues={{
              id: '',
              label: '',
              logo: '',
            }}
            onSubmit={(values) => submit(values)}
          >
            {({ isSubmitting, submitForm }) => (
              <React.Fragment>
                <Form>
                  <Field
                    name="id"
                    placeholder={t('common.identifier')}
                    as={ControlSelect}
                    disabled={isSubmitting}
                    disableSearch
                  >
                    {creatableIds.map((id, index) => (
                      <option
                        value={id}
                        key={index}
                      >
                        {id}
                      </option>
                    ))}
                  </Field>
                  <Field
                    name="label"
                    placeholder={t('common.name')}
                    as={ControlInput}
                    disabled={isSubmitting}
                  />
                  <FileUploadField
                    label={t('administration.addLogo')}
                    name="logo"
                    layout="dialog"
                    disabled={isSubmitting}
                  />
                </Form>
                <Buttons>
                  <Button
                    title={t('common.save')}
                    intent="primary"
                    onClick={submitForm}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  />
                  <Button
                    title={t('common.cancel')}
                    intent="discrete"
                    onClick={closeAll}
                    disabled={isSubmitting}
                  />
                </Buttons>
              </React.Fragment>
            )}
          </Formik>
        </Block>
      </LayoutOverlay>
    </Overlay>
  );
};

export default OverlayChannelCreate;
